import {
  Box,
  Center,
  Container,
  Flex,
  FormControl,
  FormErrorMessage,
  Image,
  Input,
  InputGroup,
  InputLeftAddon,
  Select,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useDisclosure,
} from '@chakra-ui/react'
import { useDeviceType } from 'app/hooks/useDeviceType'
import { PospImages } from 'Assets/Posp'
import CustomButton from 'Components/CustomButton'
import OtpVerificationModal from 'Components/OtpVerificationModal'
import {
  useLazyGetDetailsQuery,
  useLoginUserMutation,
  useRegisterUserMutation,
  useVerifyOTPMutation,
} from 'Services/API/posp.api'
import { ToastService } from 'Services/toast.service'
import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { updateUserBroker, updateUserDetails, updateJourneyType } from 'features/Authentication'
import { useDispatch } from 'react-redux'
import {
  LastNameRegex,
  USER_BROKER_TYPE,
  USER_REGISTRATION_TYPE,
  mobileNumRegex,
  nameRegex,
  phoneRegExp,
} from 'Constants'
import { updateUserPospStatus } from 'features/Authentication/user.slice'
import { format, parse } from 'date-fns'
import * as yup from 'yup'
import { numberRegex } from 'Constants/regex'
import { onlyAllowTypingNumbers } from 'Services/general'

interface IFormFields {
  first_name: string
  last_name: string
  gender: string
  dob: string
  phone_no: string
}

const RegisterSchema = yup.object().shape({
  first_name: yup
    .string()
    .required('Please enter first name')
    .test('len', 'Must be less than 50 characters', (val: any) => val.length <= 50)
    .test('len', 'Must be more than 2 characters', (val: any) => val.length >= 2)
    .matches(nameRegex, 'First name is not valid'),
  last_name: yup
    .string()
    .required('Please enter last name')
    .test('len', 'Must be less than 50 characters', (val: any) => val.length <= 50)
    .test('len', 'Must be more than 1 characters', (val: any) => val.length >= 1)
    .matches(LastNameRegex, 'Last name is not valid'),
  phone_no: yup
    .string()
    .test('len', 'Enter 10 digit mobile number', (val: any) => val.length === 10)
    .test('len', 'Enter valid mobile number', (val: any) => ['6', '7', '8', '9'].includes(val[0])),
  dob: yup.string().required('Please choose your date of birth'),
  gender: yup.string().required('Please select your gender'),
})

const Register = () => {
  const { isMobile, isTablet } = useDeviceType()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [isLogin, setIsLogin] = useState<boolean>(false)
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [otpInput, setOtpInput] = useState('')

  const [formFields, setFormFields] = useState<IFormFields>({
    first_name: '',
    last_name: '',
    gender: '',
    dob: '',
    phone_no: '',
  })

  const [blur, setBlur] = useState({
    first_name: false,
    last_name: false,
    gender: false,
    dob: false,
    phone_no: false,
  })

  const [loginPhoneNumber, setLoginPhoneNumber] = useState('')

  const [registerUser, { isLoading: registerLoading }] = useRegisterUserMutation()
  const [getDetails, { isLoading: getDetailsLoading }] = useLazyGetDetailsQuery()
  const [loginUser, { isLoading: loginLoading }] = useLoginUserMutation()

  const [verifyOTP, { isLoading: verifyOtpLoading }] = useVerifyOTPMutation()

  const [journeyType, setJourneyType] = useState<string>('')
  const [formValidations, setFormValidations] = useState<any>()
  const [loginFormValidation, setLoginFormValidation] = useState<any>()

  const isActionLoading = registerLoading || loginLoading || getDetailsLoading

  const handleRegisterUserClick = async () => {
    setIsLogin(false)
    setOtpInput('')
    const registrationResponse: any = await registerUser({
      ...formFields,
      training_type: journeyType,
    })
    if (registrationResponse?.data?.status === 200) {
      onOpen()
      ToastService.showInfoToast({
        title: 'Info',
        message: registrationResponse?.data?.message,
      })
    } else {
      const validationErrors = registrationResponse?.error?.data?.data
      const errorObj: any = {}
      Object.keys(validationErrors)?.forEach((key: string) => {
        errorObj[key] = validationErrors[key][0]
      })
      setFormValidations(errorObj)
    }
  }

  const handleLoginUserClick = async () => {
    setIsLogin(true)
    setOtpInput('')
    const loginResponse: any = await loginUser({
      phone_no: loginPhoneNumber,
      training_type: journeyType,
    })
    if (loginResponse?.data?.status === 200) {
      onOpen()
      ToastService.showInfoToast({
        title: 'Info',
        message: loginResponse?.data?.message,
      })
    } else {
      const validationErrors = loginResponse?.error?.data?.data
      const errorObj: any = {}
      if (validationErrors) {
        Object.keys(validationErrors)?.forEach((key: string) => {
          errorObj[key] = validationErrors[key][0]
        })
        setLoginFormValidation(errorObj)
      }
    }
  }

  const handleVerifyOtpClick = async () => {
    const verifyOTPResponse: any = await verifyOTP({
      phone_no: isLogin ? loginPhoneNumber : formFields.phone_no,
      otp: otpInput,
    })
    if (verifyOTPResponse?.data?.status === 200) {
      const getDetailsResponse = await getDetails(verifyOTPResponse?.data?.data?.user_token)
      if (getDetailsResponse?.data?.status === 200) {
        dispatch(
          updateUserDetails({
            authtoken: verifyOTPResponse.data.data.user_token,
            ...getDetailsResponse?.data?.data,
          }),
        )
        dispatch(
          updateUserPospStatus(
            getDetailsResponse?.data.data.user_type === 'registered_user'
              ? USER_REGISTRATION_TYPE.registered
              : USER_REGISTRATION_TYPE.notRegistered,
          ),
        )

        dispatch(updateUserBroker(getDetailsResponse?.data.data.user_type))
        dispatch(updateJourneyType(journeyType as 'LIFE' | 'GENERAL'))
        navigate('/registration-status')
        ToastService.showSuccessTaost({
          title: 'Success',
          message: 'Login Successful!',
        })
      }
    }
  }

  const handleErrors = (errors: any) => {
    const schemaError: any = {}
    if (Object.keys(errors).length) {
      errors.inner.forEach((error: any) => {
        schemaError[error.path] = error.message
      })
    }
    setFormValidations(schemaError)
  }

  const handleValidate = () => {
    RegisterSchema.validate(formFields, { abortEarly: false })
      .then(() => {
        handleErrors({})
      })
      .catch((err) => {
        handleErrors(err)
      })
  }

  useEffect(() => {
    handleValidate()
  }, [formFields])

  return (
    <Box>
      <Flex>
        {!isMobile && (
          <Box width='45%' bg='#efefef' h='auto' overflow={'hidden'}>
            <Flex maxW='100%' flexBasis='40%'>
              <Box w={'100%'} h='full'>
                <Image
                  w={'100%'}
                  maxW={'90%'}
                  objectFit={'contain'}
                  alt='logo'
                  src={PospImages.HomeLoginNew}
                />
              </Box>
            </Flex>
          </Box>
        )}
        <Box width={isMobile ? 'full' : '55%'} overflow='scroll' h='100vh'>
          <Container maxW={isTablet || isMobile ? 'sm' : 'md'} mt='10'>
            {isMobile && (
              <Center mb={4}>
                <Image maxW='200px' src={PospImages.ZopperLogo} alt='logo' />
              </Center>
            )}
            <Tabs variant='line' colorScheme='green' isFitted>
              <TabList borderBottom='2px solid #e2e8f0' mb='4'>
                <Tab
                  fontWeight='semibold'
                  fontSize='sm'
                  _selected={{ color: '#93C025', borderColor: '#93C025', borderBottomWidth: '2px' }}
                >
                  Register
                </Tab>
                <Tab
                  fontWeight='semibold'
                  fontSize='sm'
                  _selected={{ color: '#93C025', borderColor: '#93C025', borderBottomWidth: '2px' }}
                >
                  Login
                </Tab>
              </TabList>

              {/* Journey Type - visible in both tabs */}
              <Box mb='0'>
                <FormControl isRequired>
                  <Text fontWeight='semibold' py='2'>
                    Journey Type
                    <Text as={'span'} color={'red'}>
                      *
                    </Text>
                  </Text>
                  <Select
                    size='sm'
                    placeholder='Select Journey Type'
                    value={journeyType}
                    isDisabled={isActionLoading}
                    onChange={(e: any) => setJourneyType(e.target.value)}
                  >
                    {/* <option value='GENERAL'>General Insurance</option> */}
                    <option value='LIFE'>Life Insurance</option>
                  </Select>
                </FormControl>
              </Box>

              <TabPanels>
                {/* Register Tab */}
                <TabPanel px='0'>
                  <Box>
                    <FormControl isInvalid={blur.first_name && formValidations?.first_name}>
                      <Text fontWeight='semibold' py='2'>
                        First Name
                        <Text as={'span'} color={'red'}>
                          *
                        </Text>
                      </Text>
                      <Input
                        size='sm'
                        placeholder='Enter first name'
                        value={formFields.first_name}
                        onBlur={() => {
                          setBlur({ ...blur, first_name: true })
                          setFormFields({
                            ...formFields,
                            first_name: formFields.first_name.trim(),
                          })
                        }}
                        onChange={(e: any) => {
                          setFormFields({
                            ...formFields,
                            first_name: e.target.value,
                          })
                        }}
                      />
                      <FormErrorMessage>
                        {blur.first_name && formValidations?.first_name}
                      </FormErrorMessage>
                    </FormControl>
                  </Box>
                  <Box my='4'>
                    <FormControl isInvalid={blur.last_name && formValidations?.last_name}>
                      <Text fontWeight='semibold' py='2'>
                        Last Name
                        <Text as={'span'} color={'red'}>
                          *
                        </Text>
                      </Text>
                      <Input
                        size='sm'
                        placeholder='Enter last name'
                        value={formFields.last_name}
                        onBlur={() => {
                          setBlur({ ...blur, last_name: true })
                        }}
                        onChange={(e: any) => {
                          setFormFields({ ...formFields, last_name: e.target.value.trim() })
                        }}
                      />
                      <FormErrorMessage>
                        {blur.last_name && formValidations?.last_name}
                      </FormErrorMessage>
                    </FormControl>
                  </Box>
                  <Box>
                    <FormControl isInvalid={blur.phone_no && formValidations?.phone_no}>
                      <Text fontWeight='semibold' py='2'>
                        Mobile Number
                        <Text as={'span'} color={'red'}>
                          *
                        </Text>
                      </Text>
                      <InputGroup size='sm'>
                        <InputLeftAddon>+91</InputLeftAddon>
                        <Input
                          maxLength={10}
                          onKeyDown={onlyAllowTypingNumbers}
                          placeholder='Enter mobile number'
                          value={formFields.phone_no}
                          onBlur={() => {
                            setBlur({ ...blur, phone_no: true })
                          }}
                          onChange={(e: any) => {
                            setFormFields({
                              ...formFields,
                              phone_no: e.target.value.trim(),
                            })
                          }}
                        />
                      </InputGroup>
                      <FormErrorMessage>
                        {blur.phone_no && formValidations?.phone_no}
                      </FormErrorMessage>
                    </FormControl>
                  </Box>
                  <Box my='4'>
                    <FormControl isInvalid={blur.dob && formValidations?.dob}>
                      <Text fontWeight='semibold' py='2'>
                        Date of birth
                        <Text as={'span'} color={'red'}>
                          *
                        </Text>
                      </Text>
                      <Input
                        placeholder='DD/MM/YYYY'
                        size='sm'
                        type='date'
                        max={format(
                          parse(
                            `${new Date().getFullYear() - 18}-${
                              new Date().getMonth() + 1
                            }-${new Date().getDate()}`,
                            'yyyy-MM-dd',
                            new Date(),
                          ),
                          'yyyy-MM-dd',
                        )}
                        value={formFields.dob}
                        onBlur={() => {
                          setBlur({ ...blur, dob: true })
                        }}
                        onChange={(e: any) => {
                          setFormFields({ ...formFields, dob: e.target.value.trim() })
                        }}
                      />
                      <FormErrorMessage>{blur.dob && formValidations?.dob}</FormErrorMessage>
                    </FormControl>
                  </Box>
                  <Box>
                    <FormControl isInvalid={blur.gender && formValidations?.gender}>
                      <Flex gap={1} alignItems={'center'}>
                        <Text fontWeight='semibold' py='2'>
                          Gender
                          <Text as={'span'} color={'red'}>
                            *
                          </Text>
                        </Text>
                      </Flex>
                      <Select
                        placeholder='Select'
                        value={formFields.gender}
                        size='sm'
                        onBlur={() => {
                          setBlur({ ...blur, gender: true })
                        }}
                        onChange={(e: any) => {
                          setFormFields({ ...formFields, gender: e.target.value.trim() })
                        }}
                      >
                        <option value='1'>Male</option>
                        <option value='2'>Female</option>
                        <option value='3'>others</option>
                      </Select>
                      <FormErrorMessage>{blur.gender && formValidations?.gender}</FormErrorMessage>
                    </FormControl>
                  </Box>
                  <Box>
                    <CustomButton
                      buttonText='REGISTER'
                      buttonColor='#93C025'
                      my='30px'
                      disabled={!journeyType || !!Object.values(formValidations || {})?.length}
                      handlerFunction={handleRegisterUserClick}
                      isLoading={registerLoading || getDetailsLoading}
                    />
                  </Box>
                </TabPanel>

                {/* Login Tab */}
                <TabPanel px='0'>
                  <Box>
                    <FormControl isInvalid={loginFormValidation?.phone_no}>
                      <Text fontWeight='semibold' py='2'>
                        Mobile Number
                        <Text as={'span'} color={'red'}>
                          *
                        </Text>
                      </Text>
                      <InputGroup size='sm'>
                        <InputLeftAddon>+91</InputLeftAddon>
                        <Input
                          placeholder='Enter mobile number'
                          onChange={(e: any) => {
                            if (
                              e.target.value.length <= 10 &&
                              numberRegex.test(e.target.value) &&
                              ['6', '7', '8', '9'].includes(e.target.value[0])
                            ) {
                              setLoginPhoneNumber(e.target.value.trim())
                            }
                            if (!e.target.value) {
                              setLoginPhoneNumber('')
                            }
                          }}
                          value={loginPhoneNumber}
                          onBlur={() => {
                            setLoginFormValidation({
                              phone_no: null,
                            })
                          }}
                        />
                      </InputGroup>
                      <FormErrorMessage>{loginFormValidation?.phone_no}</FormErrorMessage>
                    </FormControl>
                    <Box>
                      <CustomButton
                        buttonText='LOGIN'
                        buttonColor='#93C025'
                        disabled={!journeyType || loginPhoneNumber.length !== 10}
                        my='30px'
                        handlerFunction={handleLoginUserClick}
                        isLoading={loginLoading}
                      />
                    </Box>
                  </Box>
                </TabPanel>
              </TabPanels>
            </Tabs>

            <Text fontSize='sm' pb='4'>
              By continuing, you agree to Zopper Insurance Brokers Private Limited Terms of Use and
              Privacy Policy
            </Text>
          </Container>
        </Box>
      </Flex>
      <OtpVerificationModal
        handleResendOTP={isLogin ? handleLoginUserClick : handleRegisterUserClick}
        isOpen={isOpen}
        onClose={onClose}
        loginPhoneNumber={isLogin ? loginPhoneNumber : formFields.phone_no}
        otpInput={otpInput}
        setOtpInput={setOtpInput}
        isLoading={verifyOtpLoading}
        verifyClickHandler={handleVerifyOtpClick}
      />
    </Box>
  )
}

export default Register
